import React, { useState, useMemo, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { useFieldArray, useForm } from "react-hook-form";
import {
  usePlans,
  useCompleteReconnectionWorkflow,
  useUpdateConsumerPlan,
  usePremises,
  useEditConsumer,
  usePremiseInfo,
  useActivateReconnectionRequest,
} from "@features/cx/reconnect/hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { Label } from "@shared/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import {
  useConsumerDetails,
  useSearchMeter,
  useServiceOrder,
  useServiceOrderConsumer,
  useDownloadReport,
  useUpdateConsumerInstallationStatus,
  useInstallationCompleted,
  useUpdateMeterInstallation,
} from "@features/cx/reconnect/hooks";
import { useDisconnectionRequestDetail } from "@features/cx/disconnect/hooks";
import { useJobCardPriority } from "@features/cx/shared/hooks";
import PaymentDialog from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/paymentModal";
import PaymentDeferredDialog from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/payLaterModal";
import PaymentReceipt from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/paymentCompletedCard";
import RateDetailsDialog from "@features/cx/consumerOnboarding/components/details/detailSteps/approval/rateModal";
import { Tabs } from "@shared/ui/tabs";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { cn } from "@shared/lib/utils";
import { toast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  MapPin,
  CreditCard,
  Wrench,
  CheckCircle2,
  Check,
  Loader2,
  Zap,
  ArrowRight,
  ArrowLeft,
  FileText,
  Download,
  Search,
  Package,
  Ruler,
} from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { reconnectionApi } from "../../../api";
import { normalizeDate } from "@shared/utils/dateUtils";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface LocationDetails {
  city: string;
  area: string;
  subArea: string;
  routeName: string;
  billingCycleName: string;
}

export interface PremiseOption {
  id: string;
  label: string;
  locationDetails?: LocationDetails;
}

const transformPremiseData = (apiData: any): PremiseOption[] => {
  const result = apiData?.result || apiData || [];
  if (!Array.isArray(result)) return [];
  return result
    .filter((p) => p?.code && p?.name)
    .map((p) => ({
      id: p.code || "",
      label: p.name || "",
      locationDetails: {
        city: p.hierarchyNames?.state || "N/A",
        area: p.hierarchyNames?.area || "N/A",
        subArea: p.hierarchyNames?.subArea || "N/A",
        routeName: "N/A",
        billingCycleName: "N/A",
      },
    }));
};

type InstallationType = "schedule" | "assign" | "no-meter";

interface UtilityServiceForm {
  utilityService: string;
  deviceNumber: string;
  latestReading: string;
  readingDate: string;
  selectedConsumer?: any;
  selectedMeter?: any;
}

interface MeterFormData {
  services: UtilityServiceForm[];
}

interface PlanActivateTabProps {
  premise: string;
  id: string;
  consumerData: any;
  currentStatus: any;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const calculateTotalFees = (plan: any): number =>
  (plan?.processingFees || 0) +
  (plan?.connectionFee || 0) +
  (plan?.securityDeposit || 0);

const getServicesList = (plan: any): string[] => {
  if (!plan?.utilityService) return [];
  if (typeof plan.utilityService === "string")
    return plan.utilityService
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean);
  if (Array.isArray(plan.utilityService))
    return plan.utilityService
      .map((s: any) => String(s).trim())
      .filter(Boolean);
  return [];
};

const safeStr = (v: unknown): string =>
  v === undefined || v === null ? "" : String(v);

// ─── Main Component ────────────────────────────────────────────────────────

const PlanActivateTab: React.FC<PlanActivateTabProps> = ({
  premise,
  id,
  consumerData,
  currentStatus,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 1. State
  const [selectedPlan, setSelectedPlan] = useState<any>(
    consumerData?.planDetails || null,
  );
  const [search, setSearch] = useState("");
  const [isPaid, setIsPaid] = useState(!!consumerData?.planDetails);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isPayDialogOpen, setIsPayDialogOpen] = useState(false);
  const [isPayLaterDialogOpen, setIsPayLaterDialogOpen] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);

  const [installationTypes, setInstallationTypes] = useState<
    Record<string, InstallationType>
  >({});
  const [showServiceOrderDetails, setShowServiceOrderDetails] = useState<
    Record<string, boolean>
  >({});
  const [serviceOrderDetails, setServiceOrderDetails] = useState<
    Record<string, any>
  >({});
  const [activeMeterService, setActiveMeterService] = useState<string>("");
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {},
  );
  const [selectedMeters, setSelectedMeters] = useState<Record<string, any>>({});
  const [latestReadings, setLatestReadings] = useState<Record<string, string>>(
    {},
  );
  const [readingDates, setReadingDates] = useState<
    Record<string, Date | undefined>
  >({});
  const [selectedServicePremises, setSelectedServicePremises] = useState<
    PremiseOption[]
  >([]);

  const meterAssignForm = useForm<any>();

  // useFieldArray form
  const { control, setValue } = useForm<MeterFormData>({
    defaultValues: { services: [] },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "services",
  });

  // 2. Data fetching
  const { data: disconnectRequestData } = useDisconnectionRequestDetail(
    Number(id),
    remoteUtilityId,
  );
  const consumerId = disconnectRequestData?.consumer?.id;
  const isCompleted = useMemo(() => {
    return (
      disconnectRequestData?.statusDisplay === "COMPLETED" ||
      currentStatus?.toLowerCase() === "completed"
    );
  }, [disconnectRequestData?.statusDisplay, currentStatus]);

  // ─── useConsumerDetails as SOURCE OF TRUTH ───────────────────────────────
  const { data: consumerDetailsData, isLoading: isLoadingConsumerDetails } =
    useConsumerDetails({
      remote_utility_id: remoteUtilityId,
      consumer_id: consumerId?.toString(),
      is_detail_info: true,
    });

  const { data: premisesData } = usePremises(Number(remoteUtilityId));
  const { data: plansData } = usePlans(remoteUtilityId);
  const { data: priorityData } = useJobCardPriority();

  // 3. Derived from consumerDetailsData (matches InstallationPage)
  const premiseCode = useMemo(() => {
    return (
      consumerDetailsData?.result?.addressData?.service?.PREMISE ||
      consumerDetailsData?.result?.addressData?.service?.premise ||
      ""
    );
  }, [consumerDetailsData]);

  const meterMappingData = useMemo(() => {
    return consumerDetailsData?.result?.consumerMappingData || [];
  }, [consumerDetailsData]);

  const utilityServices: string[] = useMemo(() => {
    const services =
      meterMappingData?.map((mapping: any) => mapping.utilityService) || [];
    return services;
  }, [meterMappingData]);

  // Tabs are driven by the SELECTED PLAN's utilities, not by meterMappingData
  const planServices: string[] = useMemo(
    () => getServicesList(selectedPlan),
    [selectedPlan],
  );

  const unassignedUtilityServices = useMemo(() => {
    const unassigned =
      meterMappingData?.filter((mapping: any) => !mapping.meterDetails) || [];
    return unassigned.map((mapping: any) => mapping.utilityService);
  }, [meterMappingData]);

  const hasUnassignedMeters = useMemo(() => {
    return (
      meterMappingData?.some((mapping: any) => !mapping.meterDetails) || false
    );
  }, [meterMappingData]);

  // URL Mapping uses plan services so tabs appear immediately on plan select
  const urlMapping = useMemo(() => {
    const base = planServices.length > 0 ? planServices : utilityServices;
    return Object.fromEntries(
      base.map((s) => [s, s.toLowerCase().replace(/\s+/g, "-")]),
    );
  }, [planServices, utilityServices]);

  // Sync state with URL
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUtilityTabParam = searchParams.get("tab1");

  const currentUtilityTab = useMemo(() => {
    if (!currentUtilityTabParam) return null;
    return (
      Object.keys(urlMapping).find(
        (key) => urlMapping[key] === currentUtilityTabParam,
      ) || currentUtilityTabParam
    );
  }, [currentUtilityTabParam, urlMapping]);

  useEffect(() => {
    if (currentUtilityTab && currentUtilityTab !== activeMeterService) {
      setActiveMeterService(currentUtilityTab);
    } else if (!activeMeterService && utilityServices.length > 0) {
      const firstService = utilityServices[0];
      setActiveMeterService(firstService);

      if (!currentUtilityTabParam) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab1", urlMapping[firstService] || firstService);
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [
    currentUtilityTab,
    currentUtilityTabParam,
    utilityServices,
    activeMeterService,
    setSearchParams,
    searchParams,
    urlMapping,
  ]);

  // 4. Plan list / filter
  const plansList: any[] = useMemo(() => {
    if (!plansData) return [];
    if (Array.isArray(plansData)) return plansData;
    if (Array.isArray((plansData as any).result))
      return (plansData as any).result;
    return [];
  }, [plansData]);

  const filteredPlans = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return plansList;
    return plansList.filter(
      (p) =>
        p.planName?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term),
    );
  }, [plansList, search]);

  // 5. Service Order fetch
  const activeServiceForFetch = activeMeterService || utilityServices[0] || "";

  const {
    data: serviceOrdersResponse,
    isLoading: isLoadingServiceOrders,
    error: serviceOrdersError,
  } = useServiceOrder({
    remote_utility_id: remoteUtilityId,
    search_data: search,
    utility_service: activeServiceForFetch,
  });

  const serviceOrderOptions = useMemo(() => {
    if (!serviceOrdersResponse?.result) return [];
    return serviceOrdersResponse.result.map((order: any) => ({
      label: ` ${order.name}`,
      value: order.id.toString(),
      data: {
        id: order.id,
        service_no: order.service_no,
        name: order.name,
        sop_type: order.sop_type,
        sop_subtype: order.sop_subtype,
        short_name: order.short_name,
        est_total_cost: order.est_total_cost,
        est_effort: order.est_effort,
      },
    }));
  }, [serviceOrdersResponse]);

  const sopToUtilityMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    const result =
      (serviceOrdersResponse as any)?.result || serviceOrdersResponse;
    if (Array.isArray(result)) {
      result.forEach((order: any) => {
        if (order.id && order.utility_service)
          mapping[order.id.toString()] = order.utility_service;
        if (order.sop_id && order.utility_service)
          mapping[order.sop_id.toString()] = order.utility_service;
      });
    }
    return mapping;
  }, [serviceOrdersResponse]);

  const currentSopId = useMemo(() => {
    const activeTab = activeMeterService || utilityServices[0];
    if (!activeTab) return "";

    const additionalData =
      consumerDetailsData?.result?.additionalData ||
      consumerDetailsData?.result?.additional_data;
    const methods =
      additionalData?.installationMethods ||
      additionalData?.installation_methods;

    if (methods) {
      const activeTabLower = activeTab.toLowerCase();
      const methodKey = Object.keys(methods).find(
        (k) => k.toLowerCase() === activeTabLower,
      );
      if (methodKey) {
        const methodData = methods[methodKey];
        const jobCardId =
          methodData?.serviceOrderId || methodData?.service_order_id;
        if (jobCardId) return jobCardId.toString();
      }
    }

    if (!meterMappingData || meterMappingData.length === 0) return "";
    const mapping = meterMappingData.find(
      (m: any) => m.utilityService === activeTab,
    );
    return (
      mapping?.sopId?.toString() ||
      mapping?.serviceOrder?.sopId?.toString() ||
      mapping?.serviceOrder?.id?.toString() ||
      mapping?.sop_id?.toString() ||
      ""
    );
  }, [
    consumerDetailsData,
    meterMappingData,
    activeMeterService,
    utilityServices,
  ]);

  const {
    data: serviceOrderConsumerData,
    isLoading: isLoadingServiceOrderConsumer,
  } = useServiceOrderConsumer({
    remoteUtilityId: safeStr(remoteUtilityId),
    consumerId: safeStr(consumerId),
    utilityService: activeMeterService || utilityServices[0],
    jobCardId: currentSopId,
  });

  const isRelevantServiceOrder = useMemo(() => {
    if (
      !serviceOrderConsumerData?.result ||
      Object.keys(serviceOrderConsumerData.result).length === 0
    ) {
      return false;
    }
    const activeTab = activeMeterService || utilityServices[0];
    const result = serviceOrderConsumerData.result;
    const normalize = (s: any) =>
      String(s || "")
        .toLowerCase()
        .trim();

    if (currentSopId && result.id?.toString() === currentSopId.toString())
      return true;
    if (serviceOrderDetails[activeTab]?.id === result.id) return true;

    const sopId = result.sop?.id || result.sop_id || result.sop;
    if (sopId && sopToUtilityMapping[sopId.toString()]) {
      const sopUtility = sopToUtilityMapping[sopId.toString()];
      if (normalize(sopUtility) === normalize(activeTab)) return true;
    }
    if (Array.isArray(result.sop?.utility_services)) {
      if (
        result.sop.utility_services.some(
          (s: string) => normalize(s) === normalize(activeTab),
        )
      ) {
        return true;
      }
    }
    const orderUtilityService =
      result.utilityService ||
      result.utility_service ||
      result.service_name ||
      result.utility_service_name ||
      result.sop?.utility_service ||
      result.sop?.service_name;
    if (orderUtilityService) {
      // Service order that belongs to an already-assigned service is NOT relevant to current tab
      const isAlreadyAssignedElsewhere = meterMappingData.some(
        (m: any) =>
          m.meterDetails &&
          normalize(m.utilityService) === normalize(orderUtilityService),
      );
      if (isAlreadyAssignedElsewhere) return false;
      return normalize(orderUtilityService) === normalize(activeTab);
    }
    if (utilityServices.length === 1) return true;
    return false;
  }, [
    serviceOrderConsumerData,
    activeMeterService,
    utilityServices,
    serviceOrderDetails,
    sopToUtilityMapping,
    currentSopId,
    meterMappingData,
  ]);

  const reportId =
    serviceOrderConsumerData?.result?.id ||
    serviceOrderConsumerData?.result?.jobCardNo ||
    currentSopId;

  const downloadReportMutation = useDownloadReport();
  const viewReportMutation = useDownloadReport();

  const premiseOptions = useMemo(
    () => transformPremiseData(premisesData),
    [premisesData],
  );

  const priorityOptions =
    priorityData?.map((priority) => ({
      value: priority.key,
      label: priority.value,
    })) || [];

  // 6. Forms + mutations
  const premiseForm = FormService.createForm({
    fields: [],
    defaultValues: {
      servicePremises: premise ? [premise] : [],
      serviceOrder: "",
      date: "",
      priority: "medium",
      note: "",
    },
  });

  const selectedServicePremiseIds = premiseForm.watch("servicePremises") || [];
  const firstServicePremiseId = selectedServicePremiseIds[0] || "";

  const { data: servicePremiseInfoData } = usePremiseInfo(
    firstServicePremiseId,
    remoteUtilityId,
  );

  const editConsumerMutation = useEditConsumer();
  const updatePlanMutation = useUpdateConsumerPlan(Number(id));
  const completeWorkflow = useCompleteReconnectionWorkflow(Number(id));

  const updateConsumerServiceMutation = useUpdateMeterInstallation();
  const updateConsumerInstallationStatusMutation =
    useUpdateConsumerInstallationStatus();
  const installationCompletedMutation = useInstallationCompleted();
  const activateRequestMutation = useActivateReconnectionRequest();

  // 7. Completion gate — all plan services must be handled before activation
  const allMetersDone = useMemo(() => {
    // Use plan services as the source of truth; fall back to consumer mapping
    const services = planServices.length > 0 ? planServices : utilityServices;
    if (services.length === 0) return false;

    return services.every((svc) => {
      // Backend persistence: already assigned or scheduled
      const mapping = meterMappingData?.find(
        (m: any) => m.utilityService === svc,
      );
      const isAlreadyDone = !!(
        mapping?.meterDetails ||
        mapping?.sopId ||
        mapping?.serviceOrder ||
        mapping?.sop_id
      );
      if (isAlreadyDone) return true;

      // Local session: meter assigned + reading filled, or scheduled, or no-meter
      const isLocallyDone = !!(
        (selectedMeters[svc] && latestReadings[svc] && readingDates[svc]) ||
        showServiceOrderDetails[svc] ||
        installationTypes[svc] === "no-meter"
      );

      return isLocallyDone;
    });
  }, [
    planServices,
    utilityServices,
    meterMappingData,
    selectedMeters,
    latestReadings,
    readingDates,
    showServiceOrderDetails,
    installationTypes,
  ]);

  // 8. Effects
  useEffect(() => {
    if (selectedServicePremiseIds.length > 0) {
      setSelectedServicePremises(
        premiseOptions.filter((p) => selectedServicePremiseIds.includes(p.id)),
      );
    } else {
      setSelectedServicePremises([]);
    }
  }, [selectedServicePremiseIds, premiseOptions]);

  useEffect(() => {
    if (consumerData?.planDetails) {
      setSelectedPlan(consumerData.planDetails);
      setIsPaid(true);
    }
  }, [consumerData]);

  useEffect(() => {
    const requestPremise =
      consumerDetailsData?.result?.addressData?.service?.PREMISE ||
      (disconnectRequestData as any)?.territoryDataCodes?.service?.premise;
    if (requestPremise && !premiseForm.getValues("servicePremises")?.length) {
      premiseForm.setValue("servicePremises", [requestPremise]);
    }
  }, [consumerDetailsData, disconnectRequestData, premiseForm]);

  useEffect(() => {
    // Seed the active tab from plan services first; fall back to consumer services
    const services = planServices.length > 0 ? planServices : utilityServices;
    if (services.length > 0 && !activeMeterService) {
      setActiveMeterService(services[0]);
    }
  }, [planServices, utilityServices, activeMeterService]);

  // Init useFieldArray + searchQueries when services change
  useEffect(() => {
    if (utilityServices.length > 0) {
      const initialServices = utilityServices.map((svc) => ({
        utilityService: svc,
        deviceNumber: "",
        latestReading: "",
        readingDate: "",
        selectedConsumer: null,
        selectedMeter: null,
      }));
      remove();
      initialServices.forEach((s) => append(s));
      const initialQueries: Record<string, string> = {};
      utilityServices.forEach((s) => {
        initialQueries[s] = "";
      });
      setSearchQueries(initialQueries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utilityServices.join("|")]);

  useEffect(() => {
    if (serviceOrdersError) {
      toast({
        title: "Error Loading Service Orders",
        description: "Failed to load service orders. Please try again.",
        variant: "destructive",
      });
    }
  }, [serviceOrdersError]);

  // 9. Search-meter hooks
  const meterQueries = useQueries({
    queries: utilityServices.map((svc) => ({
      queryKey: QueryKeyFactory.module.cx.cxonboarding.searchMeter({
        remote_utility_id: remoteUtilityId,
        utility_service: svc,
        premise: premiseCode || firstServicePremiseId || premise,
      }),
      queryFn: () =>
        reconnectionApi.searchMeter({
          remoteUtilityId: safeStr(remoteUtilityId),
          utilityService: svc,
          premise: safeStr(premiseCode || firstServicePremiseId || premise),
        }),
      enabled:
        !!remoteUtilityId &&
        !!svc &&
        !!(premiseCode || firstServicePremiseId || premise),
    })),
  });

  const meterQueriesResults = useMemo(() => {
    const results: Record<string, any> = {};
    utilityServices.forEach((svc, index) => {
      results[svc] = meterQueries[index];
    });
    return results;
  }, [utilityServices, meterQueries]);

  const getFilteredMeters = (utilityService: string) => {
    const queryResult = meterQueriesResults[utilityService];
    const list = queryResult?.data?.result ?? queryResult?.data ?? [];
    const arr = Array.isArray(list) ? list : [];
    const q = (searchQueries[utilityService] || "").toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (m: any) =>
        m.meterNumber?.toLowerCase().includes(q) ||
        m.deviceNo?.toLowerCase().includes(q),
    );
  };

  const handleSearchQueryChange = (utilityService: string, query: string) => {
    setSearchQueries((prev) => ({ ...prev, [utilityService]: query }));
  };

  const handleMeterSelect = (utilityService: string, meter: any) => {
    setSelectedMeters((prev) => ({
      ...prev,
      [utilityService]: { ...meter, meterId: meter.id },
    }));
    const idx = fields.findIndex((f) => f.utilityService === utilityService);
    if (idx >= 0) {
      setValue(`services.${idx}.deviceNumber`, meter.deviceNo || "");
      setValue(`services.${idx}.selectedMeter`, meter);
      setValue(`services.${idx}.selectedConsumer`, meter);
    }
    toast({
      title: "Meter Selected",
      description: `${utilityService} meter selected — ID: ${meter.id}`,
    });
  };

  // ─── utilityTabsConfig (PORTED FROM InstallationPage) ──────────────────
  // This is the missing piece — renders the meter search input + results table
  // and the "Selected Meter Details" block. Without this the assign branch
  // showed nothing and selectedMeters[svc] could never be set.
  const utilityTabsConfig = useMemo(() => {
    const config: Record<string, any> = {};

    utilityServices.forEach((service: string) => {
      const queryResult = meterQueriesResults[service];
      const filteredMeters = getFilteredMeters(service);
      const isLoading = queryResult?.isLoading;
      const hasError = queryResult?.error;

      const columns: TableColumn<any>[] = [
        {
          header: "Meter No",
          key: "meterNumber",
        },
        {
          header: "Device No",
          key: "deviceNo",
        },
        {
          header: "Physical Status",
          key: "physicalStatusDisplay",
        },
        {
          header: "Status",
          key: "statusDisplay",
          render: (val: any) => (
            <Badge
              variant="outline"
              className="text-green-800 bg-green-100 border-green-200"
            >
              {val}
            </Badge>
          ),
        },
        {
          header: "Action",
          key: "id",
          render: (_, meter: any) => (
            <Button
              variant="outline"
              size="sm"
              disabled={isCompleted}
              onClick={() =>
                !isCompleted &&
                handleMeterSelect(service, meter)
              }
            >
              {selectedMeters[service]?.id === meter.id
                ? "Selected"
                : "Select"}
            </Button>
          ),
        },
      ];

      config[service] = {
        component: (
          <div className="space-y-4">
            {/* Meters Table */}
            <div>
              <h3 className="text-sm font-medium mb-4 flex justify-end">
                Available {service} Meters
              </h3>
              <div className="overflow-hidden bg-white p-2">
                <GenericTable
                  data={filteredMeters || []}
                  columns={columns}
                  loading={isLoading}
                  rowKey="id"
                  emptyMessage={
                    hasError
                      ? "Error loading meters"
                      : searchQueries[service]
                        ? "No meters match your search"
                        : "No meters available"
                  }
                  onSearch={(term) => handleSearchQueryChange(service, term)}
                  searchPlaceholder={`Search by ${service.toLowerCase()} meter number or device number...`}
                  getRowClassName={(row) =>
                    cn(
                      selectedMeters[service]?.id === row.id
                        ? "bg-blue-50/50 hover:bg-blue-50"
                        : "",
                      "text-xs"
                    )
                  }
                />
              </div>
            </div>

            {/* Selected Meter Details */}
            {selectedMeters[service] && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-medium">
                  Selected {service} Meter Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Meter Number
                    </p>
                    <p className="font-medium">
                      {selectedMeters[service].meterNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Device Number
                    </p>
                    <p className="font-medium">
                      {selectedMeters[service].deviceNo}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Meter ID</p>
                    <p className="font-medium text-green-600">
                      {selectedMeters[service].meterId ||
                        selectedMeters[service].id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium">
                      {selectedMeters[service].statusDisplay}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
      };
    });

    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unassignedUtilityServices,
    meterQueriesResults,
    searchQueries,
    selectedMeters,
    isCompleted,
  ]);

  // 10. Handlers
  const handleViewReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }
    try {
      const blob = await viewReportMutation.mutateAsync({
        remoteUtilityId: safeStr(remoteUtilityId),
        reportId: safeStr(reportId),
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast({
        title: "Error viewing report",
        description: "Failed to open the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      toast({
        title: "No report available",
        description: "Report ID not found for this inspection.",
        variant: "destructive",
      });
      return;
    }
    try {
      await downloadReportMutation.mutateAsync({
        remoteUtilityId: safeStr(remoteUtilityId),
        reportId: safeStr(reportId),
      });
      toast({
        title: "Download started",
        description: "The inspection report is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the inspection report.",
        variant: "destructive",
      });
    }
  };

  const handleAddPremise = () => {
    if (!consumerId) return;
    const getHierarchy = (p: any) => {
      const hc = p?.hierarchy_codes || p?.hierarchyCodes || {};
      return {
        region: hc.region || "",
        country: hc.country || "",
        city: hc.state || "",
        county: hc.county || "",
        zone: hc.zone || "",
        division: hc.division || "",
        area: hc.area || "",
        subArea: hc.sub_area || hc.subArea || "",
        premise: hc.premise || "",
      };
    };
    const values = premiseForm.getValues();
    const payload: any = {
      scheduling: {
        serviceOrder: values.serviceOrder,
        date: values.date,
        priority: values.priority,
        note: values.note,
      },
    };
    if (selectedServicePremiseIds.length > 0) {
      const sp =
        servicePremiseInfoData?.result?.premiseInfo ||
        servicePremiseInfoData?.result?.[0] ||
        (premisesData?.result || []).find(
          (p: any) => p.code === selectedServicePremiseIds[0],
        );
      if (sp) {
        payload.serviceAddressData = {
          address: sp?.name || sp?._?.name || "",
          ...getHierarchy(sp),
        };
      }
    }
    editConsumerMutation.mutate({
      consumerId: String(consumerId),
      payload,
      type: "premises",
    });
  };

  const handlePlanSelect = (plan: any) => {
    setSelectedPlan(plan);
    if (consumerId) {
      updatePlanMutation.mutate({
        consumerId: Number(consumerId),
        payload: { remotePlanId: plan.id, completePlanData: plan } as any,
      });
    }
  };

  const handlePaymentComplete = (data: any) => {
    setIsPaid(true);
    setPaymentData(data);
    setIsPayDialogOpen(false);
    setShowReceipt(true);
    queryClient.invalidateQueries({ queryKey: ["cx", "reconnectionRequests"] });
    queryClient.invalidateQueries({
      queryKey: ["cx", "disconnectionRequests", "details"],
    });
  };

  // SCHEDULE form fields
  const scheduleInstallationFields: FormField[] = useMemo(
    () => [
      {
        name: "selectedServiceOrder",
        label: "Select Service Order",
        type: "select",
        required: true,
        options: serviceOrderOptions,
        placeholder: isLoadingServiceOrders
          ? "Loading service orders..."
          : "Select service order",
        disabled: isLoadingServiceOrders || isCompleted,
      },
      {
        name: "installationDate",
        label: "Select Installation Date",
        type: "datepicker",
        required: true,
        placeholder: "Select date",
        disabled: isCompleted,
      },
      {
        name: "priority",
        label: "Select Priority",
        type: "select",
        required: true,
        options: priorityOptions,
        placeholder: "Select Priority",
        disabled: isCompleted,
      },
      {
        name: "specialInstructions",
        label: "Special Instructions",
        type: "textarea",
        required: false,
        placeholder: "Add any special instructions for installation...",
        rows: 4,
        maxLength: 500,
        disabled: isCompleted,
      },
    ],
    [serviceOrderOptions, isLoadingServiceOrders, priorityOptions, isCompleted],
  );

  const scheduleForm = FormService.createForm({
    fields: scheduleInstallationFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const handleScheduleFormSubmit = useCallback(
    async (formData: any) => {
      const currentTab = activeMeterService || utilityServices[0];
      if (!currentTab) return;

      const { selectedServiceOrder, installationDate, specialInstructions } =
        formData;
      try {
        const selectedOrder = serviceOrderOptions.find(
          (so: any) => so.value === selectedServiceOrder,
        );
        if (!selectedOrder) {
          toast({
            title: "Error",
            description: "Selected service order not found.",
            variant: "destructive",
          });
          return;
        }

        const formattedDate = format(
          new Date(installationDate),
          "yyyy-MM-dd HH:mm:ss.SSS xxxx",
        );

        const cData = consumerDetailsData?.result || {};
        const consumerFullName =
          `${cData?.firstName ?? ""} ${cData?.lastName ?? ""}`.trim();

        const payload: any = {
          is_workorder: 1,
          remote_utility_id: remoteUtilityId,
          sop: parseInt(selectedServiceOrder),
          utility_service: currentTab,
          installation_methods: {
            [currentTab]: {
              method: "schedule_installation",
              sop_id: parseInt(selectedServiceOrder),
            },
          },
          request_account_no:
            cData.consumerNo ||
            cData.consumer_no ||
            cData.account_no ||
            cData.consumer_number ||
            "",
          request_id: cData.id || cData.consumer_id || cData.request_id || "",
          request_username:
            consumerFullName ||
            cData.consumer_name ||
            cData.customer_name ||
            "",
          mobile_number:
            cData.mobileNumber ||
            cData.mobile_number ||
            cData.contact_number ||
            cData.phone ||
            "",
          area: cData.addressData?.service?.AREA || "",
          sub_area: cData.addressData?.service?.SUBAREA || "",
          premise: cData.addressData?.service?.PREMISE || "",
          request_availability_date: formattedDate,
          remark: specialInstructions || "",
          priority: formData.priority,
        };

        await updateConsumerServiceMutation.mutateAsync({
          consumerId: String(consumerId),
          payload,
        });

        setServiceOrderDetails((prev) => ({
          ...prev,
          [currentTab]: {
            ...selectedOrder.data,
            name: selectedOrder.label,
            installationDate,
            specialInstructions,
            status: "Scheduled",
          },
        }));
        setShowServiceOrderDetails((prev) => ({
          ...prev,
          [currentTab]: true,
        }));

        toast({
          title: "Installation scheduled successfully",
          description: `Meter installation scheduled for ${format(
            new Date(installationDate),
            "PPP",
          )}.`,
        });
      } catch (error) {
        toast({
          title: "Error scheduling installation",
          description: "Failed to schedule the installation. Please try again.",
          variant: "destructive",
        });
      }
    },
    [
      activeMeterService,
      utilityServices,
      serviceOrderOptions,
      consumerDetailsData,
      remoteUtilityId,
      consumerId,
      updateConsumerServiceMutation,
    ],
  );

  const handleAssignMeters = useCallback(async () => {
    const currentTab = activeMeterService || utilityServices[0];
    if (!currentTab) return;

    const selectedMeter = selectedMeters[currentTab];
    const latestReading = latestReadings[currentTab];
    const readingDate = readingDates[currentTab];

    if (!selectedMeter) {
      toast({
        title: "No meter selected",
        description: `Please select a meter for ${currentTab} to proceed.`,
        variant: "destructive",
      });
      return;
    }
    if (!latestReading || !readingDate) {
      toast({
        title: "Incomplete information",
        description: `Please provide meter reading information for ${currentTab}.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const formattedReadingDate = readingDate;
      const utilityServiceMeterData: Record<string, any> = {
        [currentTab]: {
          meter_id: selectedMeter.meterId || selectedMeter.id,
          current_reading: latestReading,
          current_reading_date: formattedReadingDate,
        },
      };
      const payload = {
        is_metered: 1,
        utility_service: currentTab,
        utility_service_meter_data: utilityServiceMeterData,
      };

      await updateConsumerInstallationStatusMutation.mutateAsync({
        consumerId: String(consumerId),
        payload,
      });

      toast({
        title: `${currentTab} meter assigned`,
        description:
          "The meter has been assigned and installation status updated.",
      });

      const nextUndecided = unassignedUtilityServices.find(
        (s: string) =>
          s !== currentTab &&
          !(selectedMeters[s] && latestReadings[s] && readingDates[s]) &&
          !showServiceOrderDetails[s] &&
          installationTypes[s] !== "no-meter",
      );
      if (nextUndecided) {
        setActiveMeterService(nextUndecided);
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab1", urlMapping[nextUndecided] || nextUndecided);
        setSearchParams(newParams, { replace: true });
      }
    } catch (error) {
      toast({
        title: "Error assigning meters",
        description: (error as any)?.message || "Something went wrong",
        variant: "destructive",
      });
    }
  }, [
    activeMeterService,
    utilityServices,
    unassignedUtilityServices,
    selectedMeters,
    latestReadings,
    readingDates,
    showServiceOrderDetails,
    installationTypes,
    consumerId,
    updateConsumerInstallationStatusMutation,
    searchParams,
    setSearchParams,
    urlMapping,
  ]);

  const handleNoMeterConfirm = async (svc: string) => {
    try {
      const payload = {
        is_metered: 0,
        utility_service: svc,
        utility_service_meter_data: [],
      };
      await updateConsumerInstallationStatusMutation.mutateAsync({
        consumerId: String(consumerId),
        payload,
      });
      toast({ title: `No meter configuration for ${svc} saved` });

      const nextUndecided = utilityServices.find(
        (s) =>
          s !== svc &&
          !showServiceOrderDetails[s] &&
          installationTypes[s] !== "no-meter",
      );
      if (nextUndecided) {
        setActiveMeterService(nextUndecided);
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab1", urlMapping[nextUndecided] || nextUndecided);
        setSearchParams(newParams, { replace: true });
      }
    } catch (error) {
      toast({
        title: "Error saving configuration",
        variant: "destructive",
        description: (error as any)?.message || "Something went wrong",
      });
    }
  };

  const handleDeferredContinue = () => {
    setIsPayLaterDialogOpen(false);
    setIsPaid(true);
    queryClient.invalidateQueries({ queryKey: ["cx", "reconnectionRequests"] });
    queryClient.invalidateQueries({
      queryKey: ["cx", "disconnectionRequests"],
    });
    toast({ title: "Payment deferred successfully" });
  };

  const handleActivate = async () => {
    if (!consumerId) return;
    try {
      const utilityServiceMeterData: Record<string, any> = {};

      Object.keys(selectedMeters).forEach((svc) => {
        const meter = selectedMeters[svc];
        const reading = latestReadings[svc];
        const date = readingDates[svc];
        if (meter && reading && date) {
          utilityServiceMeterData[svc] = {
            meter_id: meter.meterId || meter.id,
            current_reading: reading,
            current_reading_date: date,
          };
        }
      });

      meterMappingData.forEach((mapping: any) => {
        const svc = mapping.utilityService;
        if (mapping.meterDetails && !utilityServiceMeterData[svc]) {
          utilityServiceMeterData[svc] = {
            meter_id: mapping.meterDetails.id,
            current_reading: mapping.meterDetails.currentReading,
            current_reading_date: mapping.meterDetails.currentReadingDate,
          };
        }
      });

      const payload = { utility_service_meter_data: utilityServiceMeterData };

      await installationCompletedMutation.mutateAsync({
        consumerId: String(consumerId),
        payload,
      });

      await activateRequestMutation.mutateAsync({
        requestId: String(id),
        payload: {
          request_id: Number(id),
          status: 2,
          close_date: normalizeDate(new Date().toISOString()),
        },
      });
    } catch (error) {
      console.error("Activation failed:", error);
      toast({ title: "Error activating consumer", variant: "destructive" });
    }
  };

  // ─── Section header ─────────────────────────────────────────────────────
  const renderSectionHeader = (opts: {
    icon: React.ElementType;
    title: string;
    subtitle?: string;
    iconColor?: string;
    iconBg?: string;
    badge?: React.ReactNode;
  }) => {
    const {
      icon: Icon,
      title,
      subtitle,
      iconColor = "text-blue-600",
      iconBg = "bg-blue-50",
      badge,
    } = opts;
    return (
      <div className="flex items-center gap-3 mb-5">
        <div
          className={cn(
            "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
            iconBg,
          )}
        >
          <Icon className={cn("w-4 h-4", iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-base text-gray-900">{title}</h2>
            {badge}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
    );
  };

  const renderWorkflowFooter = (
    actionButton?: React.ReactNode,
    continueButton?: React.ReactNode,
  ) => (
    <div className="space-y-6 mt-8">
      {actionButton && <div className="flex justify-end">{actionButton}</div>}
      <div className="flex justify-end">
        {continueButton || (
          <Button
            size="lg"
            className="px-14 font-black shadow-xl shadow-primary/20 h-12 rounded-xl transition-all"
            onClick={handleActivate}
            disabled={
              !allMetersDone || activateRequestMutation.isPending || isCompleted
            }
          >
            {activateRequestMutation.isPending ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <Zap className="mr-2 w-4 h-4" />
                Activate Consumer
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  // ─── Per-tab meter installation block (radio + the 3 branches) ────
  const renderMeterInstallationBlock = (svc: string) => {
    const installationType: InstallationType =
      installationTypes[svc] || "schedule";
    const fieldIndex = fields.findIndex((f) => f.utilityService === svc);

    return (
      <div className="space-y-6">
        {/* Radio: schedule / assign / no-meter */}
        <div>
          <h3 className="text-sm font-medium mb-4">
            Meter Installation for {svc}
          </h3>
          <RadioGroup
            value={installationType}
            disabled={isCompleted}
            onValueChange={(value) =>
              !isCompleted &&
              setInstallationTypes((prev) => ({
                ...prev,
                [svc]: value as InstallationType,
              }))
            }
            className="flex flex-col md:flex-row gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="schedule" id={`${svc}-schedule`} />
              <Label htmlFor={`${svc}-schedule`}>
                Schedule New Installation
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="assign" id={`${svc}-assign`} />
              <Label htmlFor={`${svc}-assign`}>Assign Existing Resource</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="no-meter"
                id={`${svc}-no-meter`}
                disabled
              />
              <Label htmlFor={`${svc}-no-meter`}>No Resource Connected</Label>
            </div>
          </RadioGroup>
        </div>

        {/* SCHEDULE branch */}
        {installationType === "schedule" && (
          <div className="space-y-6">
            <DynamicForm
              fields={scheduleInstallationFields}
              form={scheduleForm}
              onSubmit={handleScheduleFormSubmit}
              config={{
                layout: "vertical",
                spacing: "medium",
                showResetButton: false,
                showSubmitButton: false,
                classes: FIELD_CLASSES,
              }}
            />
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button
                className="bg-blue-600 hover:bg-blue-700 font-bold px-8 h-10 rounded-lg"
                disabled={
                  updateConsumerServiceMutation.isPending || isCompleted
                }
                onClick={scheduleForm.handleSubmit(handleScheduleFormSubmit)}
              >
                {updateConsumerServiceMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Scheduling…
                  </>
                ) : (
                  <>
                    Schedule Installation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ASSIGN branch — utilityTabsConfig provides the meter table + select */}
        {installationType === "assign" && (
          <div className="space-y-6">
            {utilityTabsConfig[svc]?.component}

            {selectedMeters[svc] && (
              <div className="grid grid-cols-1 gap-4 pt-6 border-t mt-6">
                <DynamicForm
                  fields={[
                    {
                      name: "latestReading",
                      label: "Latest Meter Reading",
                      type: "number",
                      required: true,
                      placeholder: "Enter current meter reading",
                      defaultValue: latestReadings[svc] || "",
                      classes: FIELD_CLASSES,
                      className: "col-span-1",
                    },
                    {
                      name: "readingDate",
                      label: "Reading Date",
                      type: "datepicker",
                      required: true,
                      placeholder: "Select date",
                      defaultValue: readingDates[svc],
                      classes: FIELD_CLASSES,
                      className: "col-span-1",
                    },
                  ]}
                  form={meterAssignForm}
                  gridColumns={2}
                  responsiveColumns={{ sm: 2, md: 2 }}
                  onFieldChange={(name, value) => {
                    if (name === "latestReading") {
                      setLatestReadings((prev) => ({
                        ...prev,
                        [svc]: value,
                      }));
                      if (fieldIndex >= 0) {
                        setValue(`services.${fieldIndex}.latestReading`, value);
                      }
                    }
                    if (name === "readingDate") {
                      setReadingDates((prev) => ({
                        ...prev,
                        [svc]: value,
                      }));
                      if (fieldIndex >= 0) {
                        setValue(
                          `services.${fieldIndex}.readingDate`,
                          value || "",
                        );
                      }
                    }
                  }}
                  config={{
                    showSubmitButton: false,
                    layout: "grid",
                    columns: 2,
                    classes: FIELD_CLASSES,
                  }}
                />
              </div>
            )}
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button
                className="bg-blue-600 hover:bg-blue-700 font-bold px-8 h-10 rounded-lg"
                disabled={
                  !selectedMeters[svc] ||
                  !latestReadings[svc] ||
                  !readingDates[svc] ||
                  updateConsumerInstallationStatusMutation.isPending ||
                  isCompleted
                }
                onClick={handleAssignMeters}
              >
                {updateConsumerInstallationStatusMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Assigning…
                  </>
                ) : (
                  <>
                    Assign Meters
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* NO METER branch */}
        {installationType === "no-meter" && (
          <div className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">
                  Proceed Without Resource
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  You are choosing to activate this {svc} service without a
                  physical resource.
                </p>
              </div>
              <div className="pt-2">
                <Button
                  variant="outline"
                  className="font-bold border-gray-300 h-10 px-6"
                  onClick={() => !isCompleted && handleNoMeterConfirm(svc)}
                  disabled={
                    updateConsumerInstallationStatusMutation.isPending ||
                    isCompleted
                  }
                >
                  {updateConsumerInstallationStatusMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Confirm & Proceed
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Already-assigned meter details (read-only summary) ─────────────────
  const renderAssignedMeterDetails = (svc: string) => {
    const mapping = meterMappingData.find(
      (m: any) => m.utilityService === svc && m.meterDetails,
    );
    if (!mapping) return null;
    const m = mapping.meterDetails;
    return (
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-5 w-5 text-blue-600" />
            <h4 className="font-medium">{svc} Resource Details</h4>
            <Badge className="ml-2 bg-green-100 text-green-800 border-none">
              Already Assigned
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Resource ID
              </p>
              <p className="text-sm font-medium">{m.meterNumber}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Serial Number
              </p>
              <p className="text-sm font-medium">{m.deviceNo}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Meter ID
              </p>
              <p className="text-sm font-medium text-green-600">{m.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Resource Type
              </p>
              <p className="text-sm font-medium">{m.meterTypeDisplay}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Physical Status
              </p>
              <Badge
                variant="outline"
                className={
                  m.physicalStatusDisplay === "Installed"
                    ? "text-green-800 bg-green-100"
                    : "text-yellow-800 bg-yellow-100"
                }
              >
                {m.physicalStatusDisplay}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Status
              </p>
              <Badge variant="outline" className="text-blue-800 bg-blue-100">
                {m.statusDisplay}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Current Reading
              </p>
              <p className="text-sm font-medium">{m.currentReading}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Reading Date
              </p>
              <p className="text-sm font-medium">{m.currentReadingDate}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Service Order Tracking Block (extracted for reuse) ────────────────
  const renderServiceOrderTracking = (s: string) => (
    <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500">
      <div className="flex items-center justify-between border-b pb-4">
        <h3 className="text-lg font-bold text-gray-900 tracking-tight">
          Service Order Tracking Details
        </h3>
      </div>

      <div className="bg-gray-50/50 rounded-2xl p-8 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Service Order</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.sop?.name ||
                serviceOrderDetails[s]?.name ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Status</p>
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-700 border-blue-100 font-bold px-3 py-1"
            >
              {serviceOrderConsumerData?.result?.statusDisplay ||
                serviceOrderDetails[s]?.status ||
                "Created"}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Priority</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.priorityDisplay ||
                serviceOrderDetails[s]?.priority ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Job Card No</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.jobCardNo || "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Created On</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.createdDate ||
                format(new Date(), "dd-MM-yyyy")}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Type</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.sopTypeDisplay ||
                "Corrective Maintenance"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Subtype</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.sopSubtypeDisplay || "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Assigned On</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.assignDate || "Not assigned"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Field Force</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.fieldForce?.name ||
                "Not assigned"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">
              Expected End Date
            </p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.expectedEndDate || "Not set"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">
              Request Availability Date
            </p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.requestAvailabilityDate ||
                serviceOrderDetails[s]?.installationDate ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">
              Account Number
            </p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.requestAccountNo ||
                consumerDetailsData?.result?.consumerNo ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Customer Name</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.requestUsername ||
                (consumerDetailsData?.result?.firstName
                  ? `${consumerDetailsData.result.firstName} ${consumerDetailsData.result.lastName || ""}`
                  : "N/A")}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Mobile Number</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.mobileNumber ||
                consumerDetailsData?.result?.mobileNumber ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Area</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.territoryData?.area || "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-500">Sub Area</p>
            <p className="text-base font-bold text-gray-900">
              {serviceOrderConsumerData?.result?.territoryData?.subArea ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1 md:col-span-2 pt-4 border-t border-gray-100 mt-2">
            <p className="text-sm font-semibold text-gray-500">Premise</p>
            <p className="text-base font-bold text-gray-900 uppercase">
              {serviceOrderConsumerData?.result?.territoryData?.premise ||
                "N/A"}
            </p>
          </div>
          <div className="space-y-1 md:col-span-2 pt-4 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-500">
              Remarks / Special Instructions
            </p>
            <p className="text-base font-medium text-gray-700 italic">
              {serviceOrderConsumerData?.result?.remark ||
                serviceOrderDetails[s]?.specialInstructions ||
                "—"}
            </p>
          </div>
        </div>
      </div>

      {renderWorkflowFooter(
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            className="h-11 px-6 rounded-xl font-bold text-gray-700 border-gray-200 hover:bg-gray-50 flex items-center gap-2"
            onClick={handleViewReport}
            disabled={
              downloadReportMutation.isPending || viewReportMutation.isPending
            }
          >
            <FileText className="w-4 h-4 text-blue-600" />
            View Report
          </Button>
          <Button
            variant="outline"
            className="h-11 px-6 rounded-xl font-bold text-gray-700 border-gray-200 hover:bg-gray-50 flex items-center gap-2"
            onClick={handleDownloadReport}
            disabled={downloadReportMutation.isPending}
          >
            <Download className="w-4 h-4 text-green-600" />
            Download PDF
          </Button>
          {!isCompleted && (
            <Button
              variant="outline"
              className="h-11 px-6 rounded-xl font-bold border-red-100 text-red-600 hover:bg-red-50"
              onClick={() =>
                setShowServiceOrderDetails((prev) => ({
                  ...prev,
                  [s]: false,
                }))
              }
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Modify Schedule
            </Button>
          )}
        </div>,
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* ── Section 1: Premise Selection ───────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl p-0 shadow-sm">
        <div className="p-4 border-b border-gray-100 bg-white">
          {renderSectionHeader({
            icon: MapPin,
            title: "Select Premises",
            subtitle:
              "Select service and billing premises for this account (Max 5 each)",
            iconColor: "text-blue-600",
            iconBg: "bg-blue-50",
          })}
        </div>

        <div className="p-4 space-y-2">
          <DynamicForm
            form={premiseForm}
            fields={[
              {
                name: "servicePremises",
                label: "Service Premises",
                type: "multi-select",
                disabled: isCompleted,
                required: true,
                placeholder: "Select service premises",
                options: premiseOptions.map((p) => ({
                  value: p.id,
                  label: p.label,
                })),
                maxSelection: 1,
                searchable: true,
              },
            ]}
            gridColumns={1}
            config={{
              showSubmitButton: false,
              classes: FIELD_CLASSES,
            }}
          />

          {selectedServicePremises.length > 0 && (
            <div className="bg-blue-50/30 border border-blue-100 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <p className="text-[10px] font-black text-blue-600  tracking-widest">
                  Service Premises Location Details (
                  {selectedServicePremises.length})
                </p>
              </div>
              {selectedServicePremises.map((p) => (
                <div key={p.id} className="space-y-4">
                  <p className="text-xs font-bold text-gray-900 border-b border-blue-100/50 pb-2">
                    {p.label}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <ValueLabel label="City" value={p.locationDetails?.city} />
                    <ValueLabel label="Area" value={p.locationDetails?.area} />
                    <ValueLabel
                      label="Sub Area"
                      value={p.locationDetails?.subArea}
                    />
                    <ValueLabel
                      label="Route Name"
                      value={
                        servicePremiseInfoData?.result?.mxData?.routeInfo
                          ?.routeName || "N/A"
                      }
                    />
                    <ValueLabel
                      label="Billing Cycle Name"
                      value={
                        servicePremiseInfoData?.result?.bxData?.billCycleName ||
                        "N/A"
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedServicePremises.length > 0 && (
            <div className="flex justify-end pt-0">
              <Button
                onClick={handleAddPremise}
                className="px-8 font-bold h-10"
                disabled={editConsumerMutation.isPending || isCompleted}
              >
                {editConsumerMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Premise"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2: Plan Selection ──────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
        {renderSectionHeader({
          icon: CreditCard,
          title: "Plan Selection",
          subtitle: "Choose a reconnection plan for this account",
          iconColor: "text-indigo-600",
          iconBg: "bg-indigo-50",
          badge: selectedPlan ? (
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 shadow-none text-[10px]">
              {selectedPlan.planName}
            </Badge>
          ) : undefined,
        })}

        {!selectedPlan ? (
          <>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                placeholder="Search for plans…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 text-sm h-9"
                disabled={isCompleted}
              />
            </div>

            <div className="max-h-[380px] overflow-y-auto pr-1 -mr-1">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredPlans.map((plan) => {
                  const isSelected = selectedPlan?.id === plan.id;
                  const services = getServicesList(plan);
                  return (
                    <Card
                      key={plan.id}
                      className={cn(
                        "relative flex flex-col cursor-pointer transition-all border",
                        isSelected
                          ? "border-primary ring-2 ring-primary/10"
                          : "hover:border-gray-300 hover:shadow-sm",
                      )}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      {isSelected && (
                        <div className="absolute top-0 right-0 bg-primary text-white p-1 rounded-tr-md rounded-bl-md">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                      <CardHeader className="pb-1 px-4 pt-3">
                        <div className="flex justify-between items-start gap-2">
                          <CardTitle className="text-xs font-bold leading-snug line-clamp-2">
                            {plan.planName}
                          </CardTitle>
                          <Badge
                            variant="outline"
                            className="text-[9px] capitalize shrink-0"
                          >
                            {plan.category}
                          </Badge>
                        </div>
                        <CardDescription className="text-[10px] line-clamp-2 mt-0.5">
                          {plan.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-grow px-4 py-2 space-y-2">
                        <div className="text-center">
                          <p className="text-lg font-black text-primary">
                            {currency} {calculateTotalFees(plan).toFixed(0)}
                          </p>
                          <p className="text-[9px] text-gray-400 uppercase font-bold">
                            Total Fees
                          </p>
                        </div>
                        {services.length > 0 && (
                          <div>
                            <p className="text-[9px] uppercase font-bold text-gray-400 mb-1">
                              Utility Services:
                            </p>
                            <ul className="space-y-0.5">
                              <li className="text-[9px] text-gray-400 flex justify-between">
                                <span>Service Name</span>
                                {/* <span>Rate Name</span> */}
                              </li>
                              {services.map((s, i) => (
                                <li
                                  key={i}
                                  className="text-[10px] flex justify-between"
                                >
                                  <span className="text-gray-700 font-medium">
                                    {s}
                                  </span>
                                  {/* <span className="text-gray-400">
                                    {plan.utilityServiceData?.rate?.[s]?.rate
                                      ?.rateName ?? "—"}
                                  </span> */}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="px-4 pb-3 pt-0">
                        <Button
                          variant={isSelected ? "default" : "outline"}
                          className="w-full h-8 text-xs font-bold"
                          disabled={isCompleted}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCompleted) handlePlanSelect(plan);
                          }}
                        >
                          {isSelected ? "Selected" : "Select Plan"}
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
                {filteredPlans.length === 0 && (
                  <div className="col-span-3 text-center py-10 text-gray-400 text-xs font-medium">
                    No plans found
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          !showReceipt && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="bg-[#F4F9FF] border border-[#E1EFFE] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E1EFFE] bg-white flex justify-between items-center">
                  <h3 className="text-sm font-bold text-gray-900">
                    Selected Plan
                  </h3>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="text-xs font-bold h-8 px-4 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      disabled={isCompleted}
                      onClick={() => {
                        if (!isCompleted) {
                          setSelectedPlan(null);
                          setIsPaid(false);
                        }
                      }}
                    >
                      Change Plan
                    </Button>
                    <Button
                      variant="outline"
                      className="text-xs font-bold h-8 px-4 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsRateModalOpen(true)}
                    >
                      View Rates
                    </Button>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  <div className="space-y-1">
                    <h4 className="text-lg font-bold text-[#1E3A8A]">
                      {selectedPlan.planName}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {selectedPlan.description || "N/A"}
                    </p>
                    <p className="text-xs font-bold text-[#3B82F6] pt-1 uppercase">
                      Billing Frequency:{" "}
                      {selectedPlan.billingFrequency || "N/A"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-bold text-[#1E3A8A]">
                      Utility Services:
                    </p>
                    <div className="space-y-1">
                      {getServicesList(selectedPlan).map((s) => (
                        <p key={s} className="text-xs text-gray-600">
                          {s}
                        </p>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4 pt-4">
                    <p className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wider">
                      Application Fee Summary
                    </p>
                    <div className="space-y-3">
                      {[
                        {
                          label: "Application Processing Fee",
                          amount: selectedPlan.processingFees,
                        },
                        {
                          label: "Service Connection Fee",
                          amount: selectedPlan.connectionFee,
                        },
                        {
                          label: "Security Deposit",
                          amount: selectedPlan.securityDeposit,
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="flex justify-between items-center"
                        >
                          <span className="text-sm text-gray-500">
                            {item.label}
                          </span>
                          <span className="text-sm font-bold text-gray-900">
                            {currency} {(item.amount || 0).toFixed(0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-5 border-t border-[#E1EFFE] flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-900">
                      Total Due
                    </span>
                    <span className="text-lg font-black text-gray-900">
                      {currency} {calculateTotalFees(selectedPlan).toFixed(0)}
                    </span>
                  </div>

                  {!isPaid && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <Button
                        size="lg"
                        className="w-full font-bold text-sm h-12 bg-[#007BFF] hover:bg-[#0069D9] text-white shadow-none rounded-lg"
                        disabled={isCompleted}
                        onClick={() => !isCompleted && setIsPayDialogOpen(true)}
                      >
                        Pay Now
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full font-bold text-sm h-12 bg-white border-gray-200 text-gray-700 shadow-none rounded-lg"
                        disabled={isCompleted}
                        onClick={() =>
                          !isCompleted && setIsPayLaterDialogOpen(true)
                        }
                      >
                        Pay Later
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        )}

        {showReceipt && selectedPlan && (
          <div className="mt-5 animate-in fade-in duration-300">
            <PaymentReceipt
              receiptId={`RC-${id}-${Date.now()}`}
              paymentMethod={paymentData?.paymentModeDisplay ?? "Digital"}
              amountPaid={calculateTotalFees(selectedPlan)}
              feeBreakdown={[
                {
                  label: "Processing Fee",
                  amount: selectedPlan.processingFees ?? 0,
                },
                {
                  label: "Connection Fee",
                  amount: selectedPlan.connectionFee ?? 0,
                },
                {
                  label: "Security Deposit",
                  amount: selectedPlan.securityDeposit ?? 0,
                },
              ]}
              totalPaid={calculateTotalFees(selectedPlan)}
              onContinue={() => setShowReceipt(false)}
              consumerDetailsData={consumerDetailsData?.result}
            />
          </div>
        )}
      </div>

      {/* ── Section 3: Meter Installation ─────────────── */}
      {selectedPlan && !showReceipt && (
        <div className="animate-in fade-in duration-300">
          <Card className="border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="p-6 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <Wrench className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-gray-900">
                    Meter Installation
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-500 mt-1">
                    Schedule a new meter installation or assign an existing
                    meter
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {isLoadingConsumerDetails || isLoadingServiceOrderConsumer ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-500 font-medium">
                    Synchronizing service data...
                  </p>
                </div>
              ) : planServices.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    The selected plan has no utility services configured.
                  </p>
                </div>
              ) : (
                <>
                  {/* One tab per utility in the selected plan */}
                  <div className="mb-6">
                    <Tabs
                      value={
                        activeMeterService ||
                        (planServices.length > 0 ? planServices[0] : "")
                      }
                      onValueChange={(val) => setActiveMeterService(val)}
                      className="w-full"
                      urlMapping={urlMapping}
                      disableUrlSync={false}
                      tabsListClassName="w-full bg-gray-50 p-1.5 h-14 rounded-xl border border-gray-100"
                      tabsListFullWidth={true}
                      level={1}
                      tabComponents={Object.fromEntries(
                        planServices.map((s) => {
                          const mapping = meterMappingData?.find(
                            (m: any) => m.utilityService === s,
                          );
                          const isAlreadyDone = !!(
                            mapping?.meterDetails ||
                            mapping?.sopId ||
                            mapping?.serviceOrder ||
                            mapping?.sop_id
                          );
                          const isLocallyDone = !!(
                            (selectedMeters[s] &&
                              latestReadings[s] &&
                              readingDates[s]) ||
                            showServiceOrderDetails[s] ||
                            installationTypes[s] === "no-meter"
                          );
                          const isDone = isAlreadyDone || isLocallyDone;

                          return [
                            s,
                            {
                              label: (
                                <div className="flex items-center gap-2">
                                  {isDone ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Wrench className="w-4 h-4 text-gray-400" />
                                  )}
                                  <span
                                    className={cn(
                                      "font-semibold text-sm",
                                      isDone ? "text-green-700" : "",
                                    )}
                                  >
                                    {s}
                                  </span>
                                </div>
                              ),
                              component: null,
                              className: cn(
                                "flex-1 rounded-lg transition-all h-11",
                                activeMeterService === s
                                  ? "bg-white text-blue-600 shadow-sm border border-gray-100"
                                  : "text-gray-500 hover:text-gray-700",
                              ),
                            },
                          ];
                        }),
                      )}
                    />
                  </div>

                  {/* Active tab content */}
                  <div className="mt-6 space-y-6">
                    {(() => {
                      const s =
                        activeMeterService ||
                        (planServices.length > 0 ? planServices[0] : "");
                      if (!s) return null;

                      const mapping = meterMappingData?.find(
                        (m: any) => m.utilityService === s,
                      );

                      // Branch 1: meter already assigned (read-only)
                      if (mapping?.meterDetails) {
                        return (
                          <>
                            {renderAssignedMeterDetails(s)}
                            {renderWorkflowFooter()}
                          </>
                        );
                      }

                      // Branch 2: service order scheduled (tracking view)
                      const hasBackendSop = !!(
                        mapping?.sopId ||
                        mapping?.serviceOrder ||
                        mapping?.sop_id
                      );
                      if (
                        showServiceOrderDetails[s] ||
                        isRelevantServiceOrder ||
                        hasBackendSop
                      ) {
                        return renderServiceOrderTracking(s);
                      }

                      // Branch 3: pending — show installation form
                      return (
                        <>
                          {renderMeterInstallationBlock(s)}
                          {renderWorkflowFooter()}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Payment Dialogs ────────────────────────────── */}
      <PaymentDialog
        open={isPayDialogOpen}
        onClose={() => setIsPayDialogOpen(false)}
        onPaymentComplete={handlePaymentComplete}
        totalDue={calculateTotalFees(selectedPlan)}
        consumerId={Number(consumerId)}
        consumerNo={
          consumerDetailsData?.result?.consumerNo ||
          disconnectRequestData?.consumer?.consumerNo ||
          ""
        }
      />
      <PaymentDeferredDialog
        open={isPayLaterDialogOpen}
        onClose={() => setIsPayLaterDialogOpen(false)}
        onContinue={handleDeferredContinue}
      />
      <RateDetailsDialog
        open={isRateModalOpen}
        onClose={() => setIsRateModalOpen(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
};

export default PlanActivateTab;
